import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { adminServices, QA_PHASE_POST_APPEAL } from '../services/adminServices';
import { useUser } from '../UserContext';
import {
  schoolHasUnsettledExternalPayment,
  buildExternalSettlementWarning,
  confirmProceedDespiteUnsettledPayment,
  canConfirmExternalPayment,
} from '../utils/paymentSettlement';
import { geminiGenerateText } from '../services/geminiService';
import AdminIcon from './AdminIcon';
import SchoolPurgeModal from '../components/SchoolPurgeModal';
import ExternalPaymentConfirmModal from '../components/ExternalPaymentConfirmModal';
import EvidenceAnalysisProgressModal from './EvidenceAnalysisProgressModal';
import { getAiPipelineMode } from '../services/aiPipelineSettings';
import { AI_PIPELINE_LEGACY, isSchemaV2Pipeline } from '../constants/aiPipelineModes';
import {
  evidenceJobToRefineryEvidence,
  isSchemaV2EvidenceReady,
} from '../services/aiPipelines/schemaV2';
import { evidenceAnalysisJobRef } from '../services/aiPipelines/schemaV2/schoolSchemaPaths';
import {
  resolveOfferCodeType,
  offerCodeTypeLabel,
  OFFER_CODE_TYPES,
} from '../constants/offerCodeTypes';
import { accreditationLevelLabel, resolveLiveAccreditationLevel } from '../utils/accreditationLevel';
import {
  canOpenRegistryReportEditor,
  resolveRegistryRefinery,
} from '../utils/registryTriage';
import AccreditationLevelDeltaMark from '../components/AccreditationLevelDeltaMark';
import { languageManifest as baseLanguageManifest } from '../locales';
import { useManagedLanguages } from '../hooks/useManagedLanguages';

const resolveLangKey = (value, manifest = baseLanguageManifest) => {
  const raw = (value || "").toString().trim().toLowerCase();
  if (!raw) return 'en';
  const aliases = {
    filipino: 'tl',
    tagalog: 'tl',
    pilipino: 'tl',
    fil: 'tl',
    ph: 'tl'
  };
  if (aliases[raw]) return aliases[raw];
  const candidates = [raw, raw.replace(/_/g, '-'), raw.split('-')[0]];
  for (const key of candidates) {
    if (manifest[key]) return key;
  }
  if (/^[a-z]{2,5}(-[a-z]{2,5})?$/.test(raw)) return raw;
  return 'en';
};

const deriveSchoolLangKey = (school, manifest = baseLanguageManifest) =>
  resolveLangKey(
    school?.currentLanguage ||
    school?.signupLanguage ||
    school?.preferredLang ||
    school?.language ||
    school?.answers?.preferredLang ||
    school?.answers?.language ||
    'en',
    manifest
  );

const SchoolRow = ({ school, onEditReport, onOpenDossier, showToast, isOpen, onToggle }) => {
  const { languageManifest } = useManagedLanguages();
  const { user, permissions, staffRecord } = useUser();
  const [confirmingExternalPay, setConfirmingExternalPay] = useState(false);
  const [showExternalConfirmModal, setShowExternalConfirmModal] = useState(false);
  const [reportExists, setReportExists] = useState(false);
  const [auditors, setAuditors] = useState([]);
  const [auditProgress, setAuditProgress] = useState({
    count: 0, 
    status: 'Draft',
    processing: null // Tracks the AI pipeline: Reading, Anonymising, Indexing, Compiling
  });
  
  const [reportRefinery, setReportRefinery] = useState({
    evidence: { step: 0, label: '' },
    survey: { step: 0, label: '' },
  });
  const [evidenceJobModalOpen, setEvidenceJobModalOpen] = useState(false);
  const [pipelineMode, setPipelineMode] = useState(AI_PIPELINE_LEGACY);
  const [evidenceJob, setEvidenceJob] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [peerReviewSigned, setPeerReviewSigned] = useState(false);
  const [reportQaPhase, setReportQaPhase] = useState(null);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [recallReason, setRecallReason] = useState('school_request');
  const [suggestionDraft, setSuggestionDraft] = useState('');
  const [translatedSuggestion, setTranslatedSuggestion] = useState('');
  const [translatedSuggestionLang, setTranslatedSuggestionLang] = useState('en');
  const [isTranslatingSuggestion, setIsTranslatingSuggestion] = useState(false);
  const [isSendingRecall, setIsSendingRecall] = useState(false);
  const [publishingToCorporate, setPublishingToCorporate] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);

  const canPurgeSchool = !!permissions?.canPurgeSchool;

  const canPublishToCorporateSite = !!permissions?.canManagePublicShowcase;

  const runWithSettlementGuard = async (actionLabel, actionFn) => {
    if (schoolHasUnsettledExternalPayment(school)) {
      const ok = confirmProceedDespiteUnsettledPayment(
        buildExternalSettlementWarning(school.officialName, actionLabel)
      );
      if (!ok) return;
    }
    await actionFn();
  };

  const handlePublishToCorporateSite = async () => {
    if (!canPublishToCorporateSite) return;
    const staffName = user?.displayName || user?.email || 'IFIP Staff';
    setPublishingToCorporate(true);
    try {
      await adminServices.publishAccreditedSchoolToPublicShowcase(school.id, staffName, {
        allowExpired: false,
      });
      showToast(`${school.officialName} is on the corporate accredited list.`);
    } catch (err) {
      const msg = err?.message || 'Could not publish to corporate site.';
      if (/24-month|validity/i.test(msg) && window.confirm(`${msg}\n\nPublish anyway for the corporate site?`)) {
        try {
          await adminServices.publishAccreditedSchoolToPublicShowcase(school.id, staffName, {
            allowExpired: true,
          });
          showToast(`${school.officialName} published (expired accreditation).`);
        } catch (inner) {
          showToast(inner?.message || msg);
        }
      } else {
        showToast(msg);
      }
    } finally {
      setPublishingToCorporate(false);
    }
  };

  const handleConfirmExternalPayment = () => {
    if (!canConfirmExternalPayment(permissions)) return;
    setShowExternalConfirmModal(true);
  };

  const submitExternalPaymentConfirm = async (details) => {
    setConfirmingExternalPay(true);
    try {
      await adminServices.confirmExternalRegistrationPayment(
        school.id,
        user?.displayName || user?.email,
        details
      );
      showToast('External payment marked as received.');
      setShowExternalConfirmModal(false);
    } catch (err) {
      showToast(err?.message || 'Could not confirm payment.');
    } finally {
      setConfirmingExternalPay(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    getAiPipelineMode().then((mode) => {
      if (!cancelled) setPipelineMode(mode);
    });
    return () => { cancelled = true; };
  }, []);

  const refinery = resolveRegistryRefinery({
    pipelineIsSchemaV2: isSchemaV2Pipeline(pipelineMode),
    evidenceJob,
    reportRefinery,
    mapEvidenceJob: evidenceJobToRefineryEvidence,
    isAnalyzing,
  });

  const canOpenReportEditor = canOpenRegistryReportEditor({
    pipelineIsSchemaV2: isSchemaV2Pipeline(pipelineMode),
    evidenceJobReady: isSchemaV2EvidenceReady(evidenceJob),
    hasEvidenceJob: Boolean(evidenceJob),
    legacyRefineryStep: reportRefinery.evidence?.step || 0,
    reportExists,
    workflowStep: school.workflowStep,
  });

  // 1. Real-time listener for the school's audit/submission progress
  useEffect(() => {
    const auditRef = doc(db, "audits", school.id);
    const unsub = onSnapshot(auditRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const completedStations = Object.values(data.stations || {}).filter(s => s.narrative?.length > 50).length;
        setAuditProgress({ 
            count: completedStations, 
            status: data.status || 'Draft',
            processing: data.processingStatus || null
        });
      }
    });
    return () => unsub();
  }, [school.id]);

  // 1b. Report doc (peer sign-off / existence). Keep legacy refinery for schema_v2 fallback.
  useEffect(() => {
    const reportRef = doc(db, "reports", school.id);
    const unsub = onSnapshot(reportRef, (snap) => {
      if (snap.exists()) {
        setReportExists(true);
        const data = snap.data();
        if (data.meta?.refinery) {
          setReportRefinery(data.meta.refinery);
        }
        setPeerReviewSigned(data.certification?.peerReviewSigned || false);
        setReportQaPhase(data.meta?.qaPhase || null);
      } else {
        setReportExists(false);
        setReportQaPhase(null);
      }
    });
    return () => unsub();
  }, [school.id]);

  // 1c. schema_v2: evidence job is source of truth for Analyse progress (independent of reports).
  useEffect(() => {
    if (!isSchemaV2Pipeline(pipelineMode)) {
      setEvidenceJob(null);
      return undefined;
    }
    const unsub = onSnapshot(evidenceAnalysisJobRef(school.id), (snap) => {
      if (!snap.exists()) {
        setEvidenceJob(null);
        return;
      }
      const job = { id: snap.id, ...snap.data() };
      setEvidenceJob(job);
    });
    return () => unsub();
  }, [school.id, pipelineMode]);

  useEffect(() => {
    if (isOpen && permissions.canManageStaff) {
      adminServices.getApprovedStaff().then(setAuditors);
    }
  }, [isOpen, permissions.canManageStaff]);

  
  const handleAssignPeerReviewer = async (reviewerUid) => {
    const reviewer = auditors.find(a => a.id === reviewerUid);
    if (!reviewer) return;
    try {
      await adminServices.assignPeerReviewer(school.id, reviewer.id, reviewer.displayName, user.displayName);
      showToast(`Second reviewer: ${reviewer.displayName}`);
    } catch (err) {
      showToast("Second reviewer assignment failed: " + err.message, "error");
    }
  };

const handleAssignAuditor = async (auditorUid) => {
    const auditor = auditors.find(a => a.id === auditorUid);
    if (!auditor) return;
    try {
      await adminServices.assignAuditor(school.id, auditor.id, auditor.displayName, user.displayName);
      showToast(`Assigned to ${auditor.displayName}`);
    } catch (err) {
      showToast("Assignment failed: " + err.message, "error");
    }
  };

  const turnData = adminServices.getAuditTurn(school, { status: auditProgress.status }, {
    meta: { refinery, qaPhase: reportQaPhase || school.qaPhase },
  });

  // 2. Workflow State Advancement (Lifecycle management)
  const advanceWorkflow = async (newStep) => {
    try {
      const schoolRef = doc(db, "schools", school.id);
      await updateDoc(schoolRef, {
        workflowStep: newStep,
        lastWorkflowUpdate: serverTimestamp()
      });
      
      await adminServices.logAction(user.displayName, `SET_STEP_${newStep.toUpperCase()}`, { 
        schoolId: school.id, 
        schoolName: school.officialName 
      });
    } catch (err) {
      showToast("Error updating workflow: " + err.message, "error");
    }
  };

  // 3. SEF Access Toggle (Activate/Deactivate access for the school)
  const handleToggleStatus = async () => {
    const newStatus = school.status === 'active' ? 'inactive' : 'active';
    const actionLabel = newStatus === 'active' ? 'ACTIVATE' : 'DEACTIVATE';
    const confirmMsg = `Are you sure you want to ${actionLabel} SEF access for ${school.officialName}?`;
    
    if (window.confirm(confirmMsg)) {
      try {
        const schoolRef = doc(db, "schools", school.id);
        await updateDoc(schoolRef, {
          status: newStatus,
          lastAdminAction: serverTimestamp()
        });
        
        await adminServices.logAction(
          user.displayName, 
          `${actionLabel}_SEF`, 
          { schoolId: school.id, schoolName: school.officialName }
        );
      } catch (err) {
        showToast("Error updating status: " + err.message, "error");
      }
    }
  };

  // 4. SEF Recall (Administrative Override to return a submission to Draft)
  const schoolLangKey = deriveSchoolLangKey(school, languageManifest);
  const schoolLanguageLabel = languageManifest[schoolLangKey]?.nativeName || languageManifest[schoolLangKey]?.label || schoolLangKey.toUpperCase();
  const needsSuggestionTranslation = recallReason === 'accreditor_suggestion' && schoolLangKey !== 'en';
  const canSendRecall = recallReason === 'school_request'
    ? true
    : Boolean(suggestionDraft.trim()) && (!needsSuggestionTranslation || Boolean(translatedSuggestion.trim()));
  const canReturnSef = permissions.canTogglePortal && auditProgress.status === 'Submitted';

  const closeRecallModal = () => {
    setShowRecallModal(false);
    setRecallReason('school_request');
    setSuggestionDraft('');
    setTranslatedSuggestion('');
    setTranslatedSuggestionLang('en');
    setIsTranslatingSuggestion(false);
    setIsSendingRecall(false);
  };

  const handleTranslateSuggestion = async () => {
    if (!suggestionDraft.trim()) {
      showToast('Please enter a message before translating.', 'error');
      return;
    }
    if (!needsSuggestionTranslation) return;
    setIsTranslatingSuggestion(true);
    try {
      const prompt = `
Translate this IFIP staff message to ${schoolLanguageLabel} (${schoolLangKey}).
Preserve the intent and tone exactly. Return only the translated message text.

Message:
${suggestionDraft.trim()}
      `.trim();
      const translatedText = (await geminiGenerateText(prompt)).trim();
      if (!translatedText) throw new Error('No translation returned.');
      setTranslatedSuggestion(translatedText);
      setTranslatedSuggestionLang(schoolLangKey);
      showToast(`Translated for ${schoolLanguageLabel}.`);
    } catch (err) {
      showToast(`Translation failed: ${err.message}`, 'error');
    } finally {
      setIsTranslatingSuggestion(false);
    }
  };

  const handleSendRecall = async () => {
    if (!canSendRecall || isSendingRecall) return;
    setIsSendingRecall(true);
    try {
      await adminServices.returnSefToSchoolForEditing({
        schoolId: school.id,
        schoolName: school.officialName,
        adminName: user?.displayName || user?.email || 'IFIP Staff',
        reason: recallReason,
        suggestionMessageEn: suggestionDraft.trim(),
        suggestionMessageLocalized: translatedSuggestion.trim(),
        localizedLangKey: translatedSuggestionLang
      });
      showToast('SEF returned to school and email queued.');
      closeRecallModal();
    } catch (err) {
      showToast(`Recall failed: ${err.message}`, 'error');
      setIsSendingRecall(false);
    }
  };

  const handleRetractReport = async () => {
    const confirmMsg = "RETRACT REPORT? This will pull the report back from Peer Review or the School and return it to 'Drafting' status for the Auditor. Proceed?";
    if (window.confirm(confirmMsg)) {
      try {
        await adminServices.retractReport(school.id, 'report_compiling', school.officialName, user.displayName);
        showToast("Report retracted to drafting.");
      } catch (err) {
        showToast("Retract failed: " + err.message, "error");
      }
    }
  };

  const handleRunAnalysis = async (e) => {
    e.stopPropagation(); 
    setIsAnalyzing(true);
    
    try {
        const mode = await getAiPipelineMode();
        await adminServices.runRefineryExtraction(school.id);
        if (isSchemaV2Pipeline(mode)) {
          setEvidenceJobModalOpen(true);
          showToast(
            "Schema v2: analysing documents on the server. Photos are never sent to AI — watch the job board."
          );
        } else {
          showToast("Analysis Complete. Data has been processed.");
        }
    } catch (err) {
        showToast("Analysis Failed: " + err.message, "error");
    } finally {
        setIsAnalyzing(false);
    }
  };

  /** Report writing starts here: create empty report shell if analysis finished without one. */
  const handleOpenReportEditor = async (e) => {
    e?.stopPropagation?.();
    if (!canOpenReportEditor) return;
    try {
      if (!reportExists) {
        await adminServices.initializeReport(school.id);
      }
      onEditReport(school.id);
    } catch (err) {
      showToast("Could not open report editor: " + (err?.message || err), "error");
    }
  };

  const copyLink = () => {
    const link = adminServices.getMagicLink(school.id);
    navigator.clipboard.writeText(link);
    showToast("MagicLink copied to clipboard!");
  };

  const handleEmailMagicLink = async (e) => {
    e.stopPropagation();
    const to = adminServices.getSchoolEmail(school);
    if (!to) {
      showToast("No institutional email on file.", "error");
      return;
    }
    if (
      !window.confirm(
        'This will send a copy of the magic link to the school lead by email. Continue?'
      )
    )
      return;
    setSendingMagicLink(true);
    try {
      await adminServices.requestSchoolPortalLinkEmail(
        school.id,
        school.officialName,
        user?.displayName || user?.email || 'Staff'
      );
      showToast(`Magic Link email queued for ${to}.`);
    } catch (err) {
      showToast(err?.message || String(err), "error");
    } finally {
      setSendingMagicLink(false);
    }
  };

  return (
    <div className={`school-row-wrapper ${isOpen ? 'open' : ''}`}>
      <div className="school-row-main" onClick={onToggle}>
        
        {/* Col 1: Identity */}
        <div className="col-name">
          <strong 
            className="dossier-link"
            onClick={(e) => { 
              e.stopPropagation(); 
              onOpenDossier(school.id); 
            }} 
          >
            {school.officialName}
          </strong>
          <span>{school.answers?.officialAddressCountry || 'Global'}</span>
          <div style={{ 
            fontSize: '0.6rem', 
            color: school.assignedAuditorName ? '#138a8a' : '#64748b', 
            marginTop: '5px', 
            fontWeight: 'bold', 
            letterSpacing: '0.5px' 
          }}>
            {school.assignedAuditorName ? `AUDITOR: ${school.assignedAuditorName.toUpperCase()}` : 'PENDING ALLOCATION'}
          </div>
        </div>
        
        {/* Col 2: Level */}
        <div className="col-level">
          <span className={`badge-level ${resolveLiveAccreditationLevel(school)}`}>
            {accreditationLevelLabel(school.accreditationLevel)}
            <AccreditationLevelDeltaMark school={school} />
          </span>
        </div>
        
        {/* Col 3: SEF Access */}
        <div className="col-status">
          <span className={`status-dot ${school.status}`}></span>
          {school.status === 'active' ? 'Portal Open' : 'Portal Closed'}
        </div>
        
        {/* Col 4: SEF Progress */}
        <div className="col-progress">
          {auditProgress.count} / 7 Stations
        </div>

        {/* Col 5: THE TURN (The Traffic Light) */}
        <div className="col-audit-status" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className={`turn-dot ${turnData.color}`} title={turnData.label}></div>
          <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: turnData.color === 'red' ? '#f87171' : '#94a3b8' }}>
            {turnData.label.toUpperCase()}
          </span>
        </div>
        
        <div className="col-action">
          <button className="btn-expander">{isOpen ? '▲' : '▼'}</button>
        </div>
      </div>

      {isOpen && (
        <section className="school-row-details">
          {schoolHasUnsettledExternalPayment(school) && (
            <section
              style={{
                margin: '0 0 16px 0',
                padding: '14px 18px',
                borderRadius: '8px',
                border: '1px solid rgba(251, 191, 36, 0.45)',
                background: 'rgba(251, 191, 36, 0.1)',
                color: '#fde68a',
                fontSize: '0.8rem',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {(() => {
                  const codeType = resolveOfferCodeType(school.appliedOffer);
                  const isProof = codeType === OFFER_CODE_TYPES.PROOF_OF_PURCHASE;
                  return isProof
                    ? `${offerCodeTypeLabel(codeType)} — payment not confirmed`
                    : 'Partner offer — payment not confirmed';
                })()}
              </strong>
              Code <code style={{ color: '#fff' }}>{school.appliedOffer?.code || '—'}</code>
              <span
                style={{
                  marginLeft: '8px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '3px 8px',
                  borderRadius: '100px',
                  border: '1px solid #d97706',
                  color: '#fbbf24',
                  verticalAlign: 'middle',
                }}
              >
                {offerCodeTypeLabel(resolveOfferCodeType(school.appliedOffer))}
              </span>
              {' '}· fee £{Number(school.appliedOffer?.feeGbp || 0).toLocaleString('en-GB')}
              . Accreditors see a warning before report actions until finance confirms receipt.
              {canConfirmExternalPayment(permissions) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmExternalPayment();
                  }}
                  disabled={confirmingExternalPay}
                  className="btn-lifecycle-green"
                  style={{ display: 'block', marginTop: '12px', width: '100%', maxWidth: '320px' }}
                >
                  {confirmingExternalPay ? 'Confirming…' : 'Confirm payment received'}
                </button>
              )}
            </section>
          )}
          <section className="command-center-grid">
            
            {/* COLUMN 1: HUMAN OUTREACH & ACCESS */}
            <div className="command-col outreach-col">
              <h4>Institutional Access</h4>
              <button 
                onClick={(e) => { e.stopPropagation(); onOpenDossier(school.id); }}
                className="btn-dossier-primary"
                style={{ width: '100%', minHeight: '48px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
              >
                <AdminIcon name="dossier" fallback="📂" /> View Full School Dossier
              </button>

              <div className="contact-card" style={{ marginBottom: '20px' }}>
                <label>Lead Contact</label>
                <p>{school.answers?.orgLead || "---"}</p>
                <a href={`mailto:${school.answers?.q3}`} className="mailto-link">
                   {school.answers?.q3 || "No Email"}
                </a>
              </div>

              <div className="portal-security" style={{ border: '1px solid rgba(19, 138, 138, 0.3)', padding: '15px', borderRadius: '8px', background: 'rgba(0,0,0,0.1)' }}>
                <label style={{ display: 'block', textAlign: 'center', marginBottom: '12px', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase' }}>Portal Security</label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <button
                    type="button"
                    onClick={handleEmailMagicLink}
                    disabled={!permissions.canViewRegistry || sendingMagicLink}
                    className="btn-nudge"
                    style={{
                      margin: 0,
                      width: '100%',
                      opacity: permissions.canViewRegistry ? 1 : 0.5,
                      fontSize: '0.7rem',
                    }}
                    title="Sends the localized portal link email to the school's registered address (same pipeline as recovery)."
                  >
                    {sendingMagicLink ? 'Sending…' : 'Send Magic Link to school'}
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="btn-nudge"
                    style={{ margin: 0, width: '100%', fontSize: '0.7rem' }}
                  >
                    Copy Magic Link
                  </button>
                  <button
                    type="button"
                    onClick={() => window.open(adminServices.getMagicLink(school.id), '_blank')}
                    className="btn-nudge"
                    style={{ margin: 0, width: '100%', fontSize: '0.7rem' }}
                  >
                    Open School Portal ↗
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    disabled={!permissions.canTogglePortal}
                    className={school.status === 'active' ? 'btn-danger-text' : 'btn-activate-text'}
                    style={{
                      opacity: permissions.canTogglePortal ? 1 : 0.5,
                      cursor: permissions.canTogglePortal ? 'pointer' : 'not-allowed',
                      margin: 0,
                      width: '100%',
                      padding: '6px 12px',
                      fontSize: '0.7rem',
                    }}
                  >
                    {school.status === 'active' ? "Disable School's Access" : 'Enable Portal Access'}
                  </button>
                  {canPublishToCorporateSite && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublishToCorporateSite();
                      }}
                      disabled={publishingToCorporate}
                      className="btn-nudge"
                      style={{
                        gridColumn: '1 / -1',
                        margin: 0,
                        width: '100%',
                        fontSize: '0.7rem',
                        opacity: publishingToCorporate ? 0.6 : 1,
                      }}
                      title="Adds or updates this school on public/schoolShowcase (auto-sync also runs on finalise and via Cloud Function)."
                    >
                      {publishingToCorporate ? 'Publishing…' : 'Publish to Corporate Website'}
                    </button>
                  )}
                  {canPurgeSchool && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPurgeModal(true);
                      }}
                      className="btn-danger-text"
                      style={{
                        gridColumn: '1 / -1',
                        margin: 0,
                        width: '100%',
                        fontSize: '0.7rem',
                        padding: '8px 12px',
                      }}
                      title="Permanently delete selected school data. Requires Purge Schools permission."
                    >
                      Purge school data…
                    </button>
                  )}
                </div>
              </div>
              <div className="security-overrides" style={{ marginTop: '20px', border: '1px solid rgba(209, 29, 126, 0.3)', padding: '15px', borderRadius: '8px', background: 'rgba(0,0,0,0.1)' }}>
                <label style={{ display: 'block', textAlign: 'center', color: '#d11d7e', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '12px', textTransform: 'uppercase' }}>
                  Administrative Overrides
                </label>
                <button 
                  onClick={() => {
                    setShowRecallModal(true);
                    setRecallReason('school_request');
                    setSuggestionDraft('');
                    setTranslatedSuggestion('');
                    setTranslatedSuggestionLang('en');
                  }} 
                  className="btn-workflow-recall"
                  disabled={!canReturnSef}
                  style={{ opacity: canReturnSef ? 1 : 0.5, width: '100%', fontSize: '0.75rem', margin: 0 }}
                  title={!permissions.canTogglePortal
                    ? "Permission required."
                    : auditProgress.status !== 'Submitted'
                      ? "SEF is already with the school (not in submitted/locked state)."
                      : "NB this deletes any data already uploaded by the school in the evidence vault. Unlocks the SEF for the school. Use this if the school requests to make an update before it is reviewed."
                  }
                >
                  Return SEF to School for Editing
                </button>
              </div>
            </div>

            {/* COLUMN 2: DATA ANALYSIS PIPELINE */}
            <div className="command-col refinery-col">
              <h4>Data Analysis Pipeline</h4>
              <button 
                onClick={handleRunAnalysis} 
                className="btn-refinery-run" 
                disabled={auditProgress.status !== 'Submitted' || isAnalyzing || !permissions.canRunAI}
                style={{ width: '100%', minHeight: '48px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: permissions.canRunAI ? 1 : 0.5 }}
                title={!permissions.canRunAI ? "Senior Accreditor status required" : ""}
              >
                {isAnalyzing ? (
                  <>
                    <div className="btn-spinner"></div>
                    <span>Processing Data...</span>
                  </>
                ) : (
                  <>
                    <AdminIcon name="ai" fallback="🤖" /> 
                    <span>Analyse School's Uploaded Evidence</span> 
                  </>
                )}
              </button>

              <div className="vertical-stepper">
                {/* TRACK A: EVIDENCE — v2 reuses steps but labels differ (legacy wording unchanged when not v2). */}
                <div className="stepper-track-label">Evidence Track</div>
                <div className={`step-item ${refinery.evidence?.step > 1 ? 'complete' : refinery.evidence?.step === 1 ? 'active' : ''}`}>
                   <div className="step-circle">1</div>
                   <div className="step-label">{isSchemaV2Pipeline(pipelineMode) ? 'Schema seeded' : 'Files Synced'}</div>
                </div>
                <div className={`step-item ${refinery.evidence?.step > 2 ? 'complete' : refinery.evidence?.step === 2 ? 'active' : ''}`}>
                   <div className="step-circle">2</div>
                   <div className="step-label">{isSchemaV2Pipeline(pipelineMode) ? 'Evidence job board' : 'Anonymisation Scan'}</div>
                </div>
                <div className={`step-item ${refinery.evidence?.step > 3 ? 'complete' : refinery.evidence?.step === 3 ? 'active' : ''}`}>
                   <div className="step-circle">3</div>
                   <div className="step-label">{isSchemaV2Pipeline(pipelineMode) ? 'File extracts' : 'Analysis Synthesised'}</div>
                </div>
                <div className={`step-item ${refinery.evidence?.step === 4 ? 'complete' : ''}`}>
                   <div className="step-circle">4</div>
                   <div className="step-label">{isSchemaV2Pipeline(pipelineMode) ? 'Ready for draft' : 'Data Stored'}</div>
                </div>

                <div style={{ margin: '15px 0', borderBottom: '1px dashed rgba(255,255,255,0.1)' }}></div>

                {/* TRACK B: QUESTIONNAIRE DATA */}
                <div className="stepper-track-label">Questionnaire Data Track</div>
                <div className={`step-item ${refinery.survey?.step > 1 ? 'complete' : refinery.survey?.step === 1 ? 'active' : ''}`}>
                   <div className="step-circle">1</div>
                   <div className="step-label">Data Synced</div>
                </div>
                <div className={`step-item ${refinery.survey?.step > 2 ? 'complete' : refinery.survey?.step === 2 ? 'active' : ''}`}>
                   <div className="step-circle">2</div>
                   <div className="step-label">Indexing Complete</div>
                </div>
                <div className={`step-item ${refinery.survey?.step > 3 ? 'complete' : refinery.survey?.step === 3 ? 'active' : ''}`}>
                   <div className="step-circle">3</div>
                   <div className="step-label">Analysis Synthesised</div>
                </div>
                <div className={`step-item ${refinery.survey?.step === 4 ? 'complete' : ''}`}>
                   <div className="step-circle">4</div>
                   <div className="step-label">Data Stored</div>
                </div>
              </div>
            </div>

            {/* COLUMN 3: WORKFLOW & CONTINUITY */}
            <div className="command-col workflow-col">
              <h4>Execution Hub</h4>
              <button 
                onClick={handleOpenReportEditor}
                className="btn-primary-action" 
                disabled={!canOpenReportEditor}
                style={{ width: '100%', height: '48px', minHeight: '48px', maxHeight: '48px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '0 12px' }}
                title={
                  canOpenReportEditor
                    ? "Open the clinical workspace to draft or refine the IFIP audit report."
                    : isSchemaV2Pipeline(pipelineMode)
                      ? "Complete evidence analysis first (job board), or open once a report already exists."
                      : "Wait until evidence refinery reaches Stored."
                }
              >
                <AdminIcon name="compile" fallback="📝" /> Open Report Editor
              </button>
              {isSchemaV2Pipeline(pipelineMode) && (refinery.evidence?.step || 0) >= 2 && (                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEvidenceJobModalOpen(true);
                  }}
                  style={{ width: '100%', marginBottom: '16px', height: '40px' }}
                  title="Reopen the schema v2 per-file evidence job board (Hide keeps the job running)."
                >
                  Evidence job progress
                </button>
              )}

              <div className="assignment-widget" style={{ marginBottom: '20px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '6px' }}>
                <label style={{ display: 'block', fontSize: '0.6rem', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase' }}>Assigned Lead Auditor</label>
                <select 
                  value={school.assignedAuditorUid || ""} 
                  onChange={(e) => handleAssignAuditor(e.target.value)}
                  disabled={!permissions.canManageStaff}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  <option value="">Unassigned</option>
                  {auditors.map(a => (
                    <option key={a.id} value={a.id}>{a.displayName}</option>
                  ))}
                </select>
              </div>

              <div className="assignment-widget" style={{ marginBottom: '20px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '6px' }}>
                <label style={{ display: 'block', fontSize: '0.6rem', color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase' }}>Assigned Second Reviewer</label>
                <select
                  value={school.assignedPeerReviewerUid || ""}
                  onChange={(e) => handleAssignPeerReviewer(e.target.value)}
                  disabled={!permissions.canManageStaff}
                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white', padding: '8px', borderRadius: '4px', fontSize: '0.8rem' }}
                >
                  <option value="">Unassigned</option>
                  {auditors
                    .filter((a) => a.permissions?.canPeerReview || a.role === 'SENIOR_ACCREDITOR' || a.role === 'SUPER_ADMIN')
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.displayName}</option>
                    ))}
                </select>
              </div>

              <div className="editing-rights-group" style={{ border: '1px solid rgba(19, 138, 138, 0.3)', padding: '15px', borderRadius: '8px', background: 'rgba(0,0,0,0.1)' }}>
                <label style={{ display: 'block', textAlign: 'center', marginBottom: '12px', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase' }}>Report Access & Editing Rights</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {school.workflowStep === 'internal_review_rejected' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(209, 29, 126, 0.12)', color: '#f9a8d4', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Peer review: revisions requested — open Report Editor for reviewer notes
                    </div>
                  )}

                  {school.workflowStep === 'appeals' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(251, 191, 36, 0.12)', color: '#fde68a', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      School appealed — amend draft, then send to QA
                    </div>
                  )}

                  {school.workflowStep === 'peer_review' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {reportQaPhase === QA_PHASE_POST_APPEAL
                        ? 'Post-appeal QA — sign off, then release to school or approve for finalisation'
                        : 'Awaiting internal peer review sign-off'}
                    </div>
                  )}

                  {school.workflowStep === 'fact_check' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(59, 130, 246, 0.12)', color: '#93c5fd', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Released to school for fact-check
                    </div>
                  )}

                  {school.workflowStep === 'accepted' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(34, 197, 94, 0.1)', color: '#4ade80', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      School accepted findings — ready to finalise
                    </div>
                  )}

                  {/* ACTION: Send amended draft to QA (post-appeal only) */}
                  {school.workflowStep === 'appeals' && (
                    <button
                      type="button"
                      onClick={async () => {
                        await adminServices.submitAppealForQa(school.id, school.officialName, user.displayName);
                        showToast('Sent to QA for post-appeal review');
                      }}
                      className="btn-workflow-step"
                      disabled={!(permissions.canDraftReport || school.assignedAuditorUid === user.uid)}
                      style={{
                        opacity: (permissions.canDraftReport || school.assignedAuditorUid === user.uid) ? 1 : 0.5,
                        width: '100%',
                        margin: 0,
                      }}
                      title="Submit your amended draft to the QA reviewer (school will not see it until QA releases or approves finalisation)."
                    >
                      Send Amended Draft to QA
                    </button>
                  )}

                  {/* ACTION 1: SUBMIT (drafting / peer-review revisions — not appeals) */}
                  {!['peer_review', 'fact_check', 'accepted', 'finalised', 'appeals'].includes(school.workflowStep) && (
                    <button 
                      onClick={async () => {
                        await adminServices.submitForPeerReview(school.id, school.officialName, user.displayName);
                        showToast("Submitted for Internal Review");
                      }} 
                      className="btn-workflow-step"
                      disabled={!permissions.canPeerReview}
                      style={{ opacity: permissions.canPeerReview ? 1 : 0.5, width: '100%', margin: 0 }}
                      title="Locks your edits and sends the draft to the IFIP Quality Assurance team for review."
                    >
                      Submit for Internal Review
                    </button>
                  )}

                  {/* ACTION 2: RELEASE (initial QA — first school fact-check) */}
                  {school.workflowStep === 'peer_review' && reportQaPhase !== QA_PHASE_POST_APPEAL && (
                    <button 
                      onClick={async () => {
                        await runWithSettlementGuard('release draft for fact-check', async () => {
                          await adminServices.releaseToSchool(school.id, school.officialName, user.displayName);
                          showToast("Report Released for Fact-Check");
                        });
                      }} 
                      className="btn-workflow-step"
                      disabled={!permissions.canPublish || !peerReviewSigned}
                      style={{ opacity: (permissions.canPublish && peerReviewSigned) ? 1 : 0.5, width: '100%', margin: 0 }}
                      title={!peerReviewSigned ? "Internal Peer Review sign-off required" : "Release the draft report to the school for factual accuracy checking."}
                    >
                      Release Draft to School
                    </button>
                  )}

                  {/* Post-appeal QA: another fact-check OR approve for finalisation */}
                  {school.workflowStep === 'peer_review' && reportQaPhase === QA_PHASE_POST_APPEAL && (
                    <>
                      <button
                        type="button"
                        onClick={async () => {
                          await runWithSettlementGuard('release revised draft for fact-check', async () => {
                            await adminServices.releaseToSchool(school.id, school.officialName, user.displayName);
                            showToast('Revised draft released to school for fact-check');
                          });
                        }}
                        className="btn-workflow-step"
                        disabled={!(permissions.canPeerReview || permissions.canPublish) || !peerReviewSigned}
                        style={{
                          opacity: (peerReviewSigned && (permissions.canPeerReview || permissions.canPublish)) ? 1 : 0.5,
                          width: '100%',
                          margin: 0,
                        }}
                        title={!peerReviewSigned ? 'QA sign-off required' : 'Send revised draft back to the school for another fact-check pass.'}
                      >
                        Send to School for Fact-Check
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Approve this amended draft for finalisation without another school fact-check?')) return;
                          await runWithSettlementGuard('approve for finalisation', async () => {
                            await adminServices.approveAppealForFinalisation(school.id, school.officialName, user.displayName);
                            showToast('Approved for finalisation');
                          });
                        }}
                        className="btn-lifecycle-green"
                        disabled={!(permissions.canPeerReview || permissions.canPublish) || !peerReviewSigned}
                        style={{
                          opacity: (peerReviewSigned && (permissions.canPeerReview || permissions.canPublish)) ? 1 : 0.5,
                          width: '100%',
                          margin: '8px 0 0 0',
                        }}
                        title={!peerReviewSigned ? 'QA sign-off required' : 'Skip further school review and move to finalisation.'}
                      >
                        Approve for Finalisation
                      </button>
                    </>
                  )}

                  {/* ACTION 3: PULL BACK (Visible if report is with Reviewer or School) */}
                  {['peer_review', 'fact_check', 'accepted'].includes(school.workflowStep) && (
                    <button 
                      onClick={handleRetractReport} 
                      className="btn-workflow-recall"
                      disabled={!permissions.canTogglePortal}
                      style={{ opacity: permissions.canTogglePortal ? 1 : 0.5, width: '100%', fontSize: '0.75rem', margin: 0 }}
                      title="Returns editing rights to the accreditor. Use this if the Accreditor needs to make further edits."
                    >
                      Pull report back to Accreditor
                    </button>
                  )}

                  {/* ACTION 4: FINALISE (school accepted or QA approved post-appeal) */}
                  {school.workflowStep === 'accepted' && (
                    <button 
                      onClick={async () => {
                        if (!window.confirm("FINAL PUBLICATION: This will lock the report and issue the certificate. Proceed?")) return;
                        await runWithSettlementGuard('finalise report and release certificate', async () => {
                          await adminServices.finalizeAccreditation(school.id, user);
                          showToast("Cycle Finalised & Published");
                        });
                      }}
                      className="btn-lifecycle-green"
                      disabled={!(permissions.canPublish || permissions.canPeerReview)}
                      style={{ opacity: (permissions.canPublish || permissions.canPeerReview) ? 1 : 0.5, width: '100%', margin: '8px 0 0 0' }}
                      title="Finalise the cycle, issue the certificate, and lock all reports to the archive."
                    >
                      Finalise Report & Release Certificate
                    </button>
                  )}

                  {/* STATUS INDICATOR: FINALISED */}
                  {school.workflowStep === 'finalised' && (
                    <div style={{ padding: '10px', textAlign: 'center', background: 'rgba(19, 138, 138, 0.1)', color: '#138a8a', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Accreditation Cycle Complete
                    </div>
                  )}
                </div>
              </div>
            </div> {/* Closes command-col workflow-col */}

          </section> {/* Closes command-center-grid */}
        </section> /* Closes school-row-details */
      )}
      {showRecallModal && (
        <div
          onClick={closeRecallModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(760px, 95vw)',
              background: '#0f172a',
              border: '1px solid rgba(19, 138, 138, 0.4)',
              borderRadius: '10px',
              boxShadow: '0 24px 64px rgba(2, 6, 23, 0.6)',
              color: '#e2e8f0',
              padding: '20px'
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', color: '#f8fafc' }}>Reason for returning to school?</h3>
            <p style={{ margin: '0 0 14px 0', color: '#94a3b8', fontSize: '0.86rem' }}>
              This unlocks the SEF, resets refinery outputs, and removes distilled AI facts.
            </p>
            <select
              value={recallReason}
              onChange={(e) => {
                const value = e.target.value;
                setRecallReason(value);
                setTranslatedSuggestion('');
                setTranslatedSuggestionLang('en');
              }}
              style={{
                width: '100%',
                marginBottom: '16px',
                background: '#020617',
                border: '1px solid #334155',
                color: '#e2e8f0',
                padding: '10px',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            >
              <option value="school_request">School&apos;s Request</option>
              <option value="accreditor_suggestion">Accreditor&apos;s Suggestion</option>
            </select>

            {recallReason === 'accreditor_suggestion' && (
              <>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.82rem', color: '#cbd5e1' }}>
                  Email wrapper is fixed as:
                </p>
                <div style={{ marginBottom: '12px', fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.4 }}>
                  <div><strong style={{ color: '#cbd5e1' }}>Top:</strong> Message from IFIP. Your self-evaluation form has been unlocked and returned to you because:</div>
                  <div><strong style={{ color: '#cbd5e1' }}>Bottom:</strong> Best wishes, IFIP</div>
                </div>
                <textarea
                  value={suggestionDraft}
                  onChange={(e) => {
                    setSuggestionDraft(e.target.value);
                    if (translatedSuggestion) {
                      setTranslatedSuggestion('');
                      setTranslatedSuggestionLang('en');
                    }
                  }}
                  rows={5}
                  placeholder="Please include a message for the school, eg: Your comment for station three would benefit from extra detail; this would push it to the next level and the evidence seems to support that."
                  style={{
                    width: '100%',
                    background: '#020617',
                    border: '1px solid #334155',
                    color: '#e2e8f0',
                    padding: '10px',
                    borderRadius: '6px',
                    resize: 'vertical',
                    marginBottom: '12px'
                  }}
                />

                {needsSuggestionTranslation && (
                  <div style={{ marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={handleTranslateSuggestion}
                      disabled={isTranslatingSuggestion || !suggestionDraft.trim()}
                      className="btn-workflow-step"
                      style={{
                        opacity: (!suggestionDraft.trim() || isTranslatingSuggestion) ? 0.55 : 1,
                        margin: 0,
                        width: '100%'
                      }}
                    >
                      {isTranslatingSuggestion ? `Translating to ${schoolLanguageLabel}...` : `Translate for school (${schoolLanguageLabel})`}
                    </button>
                  </div>
                )}

                {needsSuggestionTranslation && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.78rem', color: '#93c5fd', fontWeight: 700 }}>
                      Translation preview ({schoolLanguageLabel})
                    </label>
                    <div
                      style={{
                        background: '#0b1220',
                        border: '1px solid rgba(147, 197, 253, 0.35)',
                        borderRadius: '6px',
                        minHeight: '84px',
                        padding: '10px',
                        color: translatedSuggestion ? '#dbeafe' : '#64748b',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {translatedSuggestion || 'Translation will appear here before sending.'}
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={closeRecallModal}
                disabled={isSendingRecall}
                style={{
                  flex: 1,
                  background: '#1e293b',
                  color: '#e2e8f0',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  cursor: isSendingRecall ? 'not-allowed' : 'pointer',
                  opacity: isSendingRecall ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendRecall}
                disabled={!canSendRecall || isSendingRecall}
                className="btn-workflow-recall"
                style={{
                  flex: 1,
                  margin: 0,
                  borderRadius: '6px',
                  opacity: (!canSendRecall || isSendingRecall) ? 0.55 : 1,
                  cursor: (!canSendRecall || isSendingRecall) ? 'not-allowed' : 'pointer'
                }}
              >
                {isSendingRecall ? 'Sending...' : 'SEND TO SCHOOL'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showPurgeModal && (
        <SchoolPurgeModal
          schoolId={school.id}
          fallbackName={school.officialName}
          onClose={() => setShowPurgeModal(false)}
          onSuccess={(result) => {
            setShowPurgeModal(false);
            showToast(`Purge completed for ${school.officialName || school.id}.`);
            if (result?.targets?.schoolRecord) {
              onToggle?.(school.id);
            }
          }}
          onError={(message) => showToast(message)}
        />
      )}
      <ExternalPaymentConfirmModal
        school={school}
        isOpen={showExternalConfirmModal}
        isSubmitting={confirmingExternalPay}
        onClose={() => !confirmingExternalPay && setShowExternalConfirmModal(false)}
        onConfirm={submitExternalPaymentConfirm}
      />
      <EvidenceAnalysisProgressModal
        schoolId={school.id}
        schoolName={school.officialName}
        open={evidenceJobModalOpen}
        onHide={() => setEvidenceJobModalOpen(false)}
      />
    </div> /* Closes school-row-wrapper */
  );
};

export default SchoolRow;
